import { useState, useEffect, useRef } from 'react';
import { getVessels } from './api';
import API from './api';

// Fields required for VSAD plan generation — used for completeness scoring
const VSAD_REQUIRED = ['ship_name','imo_number','flag','ship_type','gross_tonnage','class_register','owner_manager'];
const VSAD_RECOMMENDED = ['call_sign','mmsi','port_of_registry','net_tonnage','deadweight','loa','beam','hull_type','year_of_build','owner_address'];

function vesselCompleteness(v) {
  const reqFilled = VSAD_REQUIRED.filter(f => v[f] && String(v[f]).trim()).length;
  const recFilled = VSAD_RECOMMENDED.filter(f => v[f] && String(v[f]).trim()).length;
  const total = VSAD_REQUIRED.length + VSAD_RECOMMENDED.length;
  const filled = reqFilled + recFilled;
  return { pct: Math.round((filled / total) * 100), reqFilled, reqTotal: VSAD_REQUIRED.length, missingRequired: VSAD_REQUIRED.filter(f => !v[f] || !String(v[f]).trim()) };
}

const PLANS = [
  { code: 'GMP',       label: 'Garbage Management Plan',    count: '1 file' },
  { code: 'GMP_ANN',   label: 'GMP Annexes',                count: '6 files' },
  { code: 'BioMP',     label: 'Biofouling Management Plan', count: '1 file' },
  { code: 'BioRB',     label: 'Biofouling Record Book',     count: '1 file' },
  { code: 'RECOVERY',  label: 'Recovery Plan',              count: '1 file' },
  { code: 'SOPEP',     label: 'SOPEP',                      count: '1 file' },
  { code: 'SOPEP_ANN', label: 'SOPEP Annexes',              count: '1 file' },
  { code: 'SSP',       label: 'SSP Ship Particular Cards',  count: '16 files + merged PDF', highlight: true },
];

export default function VSADGenerator({ setPage, onLogout, setEditVessel }) {
  const [activeTab, setActiveTab] = useState('generate');
  const [showGuide, setShowGuide] = useState(false);

  // Generate tab state
  const [vessels, setVessels] = useState([]);
  const [selectedVessel, setSelectedVessel] = useState(null);
  const [selectedPlans, setSelectedPlans] = useState([]);
  const [formats, setFormats] = useState(['pdf']);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Templates tab state
  const [templates, setTemplates] = useState([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState('');
  const [uploadError, setUploadError] = useState('');
  const [pendingUpload, setPendingUpload] = useState(null);
  const [expandedPlan, setExpandedPlan] = useState(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    getVessels().then(r => setVessels(r.data)).catch(() => onLogout());
  }, []);

  useEffect(() => {
    if (activeTab === 'templates') loadTemplates();
  }, [activeTab]);

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      const r = await API.get('/vsad/templates');
      setTemplates(r.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
    } finally {
      setTemplatesLoading(false);
    }
  };

  const handleUploadClick = (plan_code, filename) => {
    setPendingUpload({ plan_code, filename });
    setUploadSuccess('');
    setUploadError('');
    fileInputRef.current.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !pendingUpload) return;
    e.target.value = '';
    setUploadingFile(pendingUpload.filename);
    setUploadSuccess('');
    setUploadError('');
    const formData = new FormData();
    formData.append('file', file);
    try {
      await API.post(
        `/vsad/templates/upload?plan_code=${pendingUpload.plan_code}&filename=${encodeURIComponent(pendingUpload.filename)}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setUploadSuccess(`${pendingUpload.filename} successfully uploaded.`);
      await loadTemplates();
    } catch (e) {
      setUploadError(e.response?.data?.detail || 'Upload failed.');
    } finally {
      setUploadingFile(null);
      setPendingUpload(null);
    }
  };

  const togglePlan = (code) => {
    setSelectedPlans(p => p.includes(code) ? p.filter(x => x !== code) : [...p, code]);
  };

  const toggleFormat = (fmt) => {
    setFormats(f => f.includes(fmt) ? f.filter(x => x !== fmt) : [...f, fmt]);
  };

  const selectAll = () => setSelectedPlans(PLANS.map(p => p.code));
  const clearAll = () => setSelectedPlans([]);

  const handleGenerate = async () => {
    if (!selectedVessel) { setError('Please select a vessel.'); return; }
    if (!selectedPlans.length) { setError('Please select at least one plan.'); return; }
    if (!formats.length) { setError('Please select at least one format.'); return; }
    setError('');
    setLoading(true);
    setResult(null);
    try {
      const res = await API.post('/vsad/generate', {
        imo_number: selectedVessel.imo_number,
        plans: selectedPlans,
        formats,
      });
      setResult(res.data);
    } catch (e) {
      if (e.response?.status === 401) onLogout();
      setError(e.response?.data?.detail || 'Generation failed.');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!result?.zip_name) return;
    const token = localStorage.getItem('token');
    window.open(`/api/vsad/download/${result.zip_name}?token=${token}`, '_blank');
  };

  const totalFiles = selectedPlans.reduce((acc, code) => {
    const plan = PLANS.find(p => p.code === code);
    if (!plan) return acc;
    if (code === 'SSP') return acc + 16;
    if (code === 'GMP_ANN') return acc + 6;
    return acc + 1;
  }, 0);

  const formatBytes = (bytes) => {
    if (!bytes) return '—';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1048576) return (bytes / 1024).toFixed(0) + ' KB';
    return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  return (
    <div style={s.wrapper}>
      <header style={s.header}>
        <button style={s.backBtn} onClick={() => setPage('dashboard')}>← Back</button>
        <div style={s.headerTitle}>VSAD Generator</div>
        <div style={s.tabBar}>
          <button
            style={{ ...s.tab, ...(activeTab === 'generate' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('generate')}
          >GENERATE</button>
          <button
            style={{ ...s.tab, ...(activeTab === 'templates' ? s.tabActive : {}) }}
            onClick={() => setActiveTab('templates')}
          >TEMPLATES</button>
        </div>
        <button style={s.guideBtn} onClick={() => setShowGuide(true)}>? How to Guide</button>
        <div style={s.headerBadge}>PLAN PRODUCTION</div>
      </header>

      {/* ── HOW TO GUIDE MODAL ── */}
      {showGuide && (
        <div style={s.overlay} onClick={() => setShowGuide(false)}>
          <div style={s.modal} onClick={e => e.stopPropagation()}>

            {/* Modal header */}
            <div style={s.modalHeader}>
              <div>
                <div style={s.modalTitle}>VSAD Generator — How to Guide</div>
                <div style={s.modalSubtitle}>Vessel Ship-specific plans And Documents</div>
              </div>
              <button style={s.modalClose} onClick={() => setShowGuide(false)}>✕</button>
            </div>

            <div style={s.modalBody}>

              {/* What is it */}
              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>What is VSAD Generator?</div>
                <div style={s.guideSectionText}>
                  VSAD Generator automates the production of vessel-specific mandatory maritime documents.
                  It takes your uploaded <strong style={s.hl}>.docx templates</strong> and merges them with the
                  vessel's official particulars (name, IMO number, flag, GT, class, etc.) to produce
                  ready-to-use Word and/or PDF files — packed into a single ZIP for download.
                </div>
              </div>

              {/* Workflow */}
              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Step-by-Step Workflow</div>
                <div style={s.steps}>
                  {[
                    {
                      n: '1',
                      title: 'Upload Templates (Templates tab)',
                      text: 'Go to the TEMPLATES tab first. Each plan type has one or more required .docx template files. Click "Upload" next to each file. Templates must be .docx format — they contain placeholders that will be filled with vessel data during generation.',
                    },
                    {
                      n: '2',
                      title: 'Select a Vessel',
                      text: 'In the GENERATE tab, pick a vessel from the dropdown. The vessel\'s registered particulars (flag, type, GT, class society) are pulled from the vessel database and shown below the selector.',
                    },
                    {
                      n: '3',
                      title: 'Select Plans',
                      text: 'Tick one or more plan types from the list. Use "All" to select everything at once. Each plan shows how many files it will produce.',
                    },
                    {
                      n: '4',
                      title: 'Choose Output Format',
                      text: 'Select WORD (.docx) for editable copies, PDF for final read-only copies, or both. Both options can be selected simultaneously.',
                    },
                    {
                      n: '5',
                      title: 'Generate & Download',
                      text: 'Click GENERATE PLANS. The system fills in every template with vessel-specific data. When complete, click DOWNLOAD ZIP to receive all generated files in a single archive.',
                    },
                  ].map(step => (
                    <div key={step.n} style={s.step}>
                      <div style={s.stepNum}>{step.n}</div>
                      <div>
                        <div style={s.stepTitle}>{step.title}</div>
                        <div style={s.stepText}>{step.text}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Plans reference */}
              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Available Plans</div>
                <div style={s.plansTable}>
                  {[
                    { code: 'GMP',       files: '1 file',            reg: 'MARPOL Annex V',   desc: 'Garbage Management Plan — procedures for garbage handling, segregation, and disposal.' },
                    { code: 'GMP_ANN',   files: '6 files',           reg: 'MARPOL Annex V',   desc: 'GMP Annexes — supplementary forms and record sheets to accompany the GMP.' },
                    { code: 'BioMP',     files: '1 file',            reg: 'IMO BWM / GloBallast', desc: 'Biofouling Management Plan — antifouling management procedures per IMO guidelines.' },
                    { code: 'BioRB',     files: '1 file',            reg: 'IMO BWM',           desc: 'Biofouling Record Book — logbook to record all hull cleaning and inspection activities.' },
                    { code: 'RECOVERY',  files: '1 file',            reg: 'SOLAS Ch. III',     desc: 'Recovery Plan — procedures for recovery of persons from water.' },
                    { code: 'SOPEP',     files: '1 file',            reg: 'MARPOL Annex I',   desc: 'Shipboard Oil Pollution Emergency Plan — ship-specific oil spill response plan.' },
                    { code: 'SOPEP_ANN', files: '1 file',            reg: 'MARPOL Annex I',   desc: 'SOPEP Annexes — contact lists and supplementary forms for the SOPEP.' },
                    { code: 'SSP',       files: '16 files + PDF',    reg: 'ISPS Code Part A', desc: 'Ship Security Plan — full set of 16 individual SSP section cards, merged into one PDF.' },
                  ].map(p => (
                    <div key={p.code} style={s.planRow}>
                      <div style={s.planRowLeft}>
                        <span style={s.planRowCode}>{p.code}</span>
                        <span style={s.planRowFiles}>{p.files}</span>
                      </div>
                      <div style={s.planRowRight}>
                        <div style={s.planRowDesc}>{p.desc}</div>
                        <div style={s.planRowReg}>{p.reg}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Notes */}
              <div style={s.guideSection}>
                <div style={s.guideSectionTitle}>Important Notes</div>
                <div style={s.noteList}>
                  {[
                    'Templates must be uploaded before generation. A missing template will cause that plan to fail while others still complete.',
                    'SSP is the most comprehensive plan: it produces 16 individual section files and one merged PDF combining all sections.',
                    'Vessel particulars (name, IMO, flag, GT, class) are automatically injected — ensure vessel records are up to date in the vessel database.',
                    'The output ZIP is named with the vessel name and timestamp, making it audit-trail friendly.',
                    'Existing generated files are not stored on the server — always download immediately after generation.',
                  ].map((note, i) => (
                    <div key={i} style={s.noteItem}>
                      <span style={s.noteDot}>—</span>
                      <span>{note}</span>
                    </div>
                  ))}
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* ── GENERATE TAB ── */}
      {activeTab === 'generate' && (
        <main style={s.main}>
          <div style={s.layout}>

            {/* LEFT */}
            <div style={s.leftCol}>
              <div style={s.card}>
                <div style={s.cardTitle}>01 — SELECT VESSEL</div>
                <select
                  style={s.select}
                  value={selectedVessel?.imo_number || ''}
                  onChange={e => {
                    const v = vessels.find(v => v.imo_number === e.target.value);
                    setSelectedVessel(v || null);
                    setResult(null);
                  }}
                >
                  <option value="">— Select vessel —</option>
                  {vessels.map(v => (
                    <option key={v.imo_number} value={v.imo_number}>
                      {v.ship_name} — IMO {v.imo_number}
                    </option>
                  ))}
                </select>

                {selectedVessel && (() => {
                  const { pct, missingRequired } = vesselCompleteness(selectedVessel);
                  return (
                    <div style={s.vesselSummary}>
                      <div style={s.summaryRow}><span style={s.summaryLabel}>Flag</span><span>{selectedVessel.flag || '—'}</span></div>
                      <div style={s.summaryRow}><span style={s.summaryLabel}>Type</span><span>{selectedVessel.ship_type || '—'}</span></div>
                      <div style={s.summaryRow}><span style={s.summaryLabel}>GT</span><span>{selectedVessel.gross_tonnage || '—'}</span></div>
                      <div style={s.summaryRow}><span style={s.summaryLabel}>Class</span><span>{selectedVessel.class_register || '—'}</span></div>
                      <div style={{ ...s.summaryRow, marginTop: '8px', paddingTop: '8px', borderTop: '1px solid var(--navy-border)' }}>
                        <span style={s.summaryLabel}>Data completeness</span>
                        <span style={{ fontFamily: 'var(--font-mono)', fontWeight: '700', color: pct === 100 ? 'var(--green)' : pct >= 70 ? 'var(--gold)' : '#ff6b7a' }}>{pct}%</span>
                      </div>
                      {missingRequired.length > 0 && (
                        <div style={s.completenessWarn}>
                          ⚠ Missing required fields: {missingRequired.join(', ')}
                        </div>
                      )}
                    </div>
                  );
                })()}
              </div>

              <div style={s.card}>
                <div style={s.cardTitle}>03 — OUTPUT FORMAT</div>
                <div style={s.formatRow}>
                  {[
                    { key: 'word', icon: '📝', label: 'WORD', desc: 'Editable' },
                    { key: 'pdf',  icon: '📄', label: 'PDF',  desc: 'Final copy' },
                  ].map(fmt => (
                    <div
                      key={fmt.key}
                      style={{ ...s.formatCard, ...(formats.includes(fmt.key) ? s.formatCardActive : {}) }}
                      onClick={() => toggleFormat(fmt.key)}
                    >
                      <div style={s.formatIcon}>{fmt.icon}</div>
                      <div style={s.formatLabel}>{fmt.label}</div>
                      <div style={s.formatDesc}>{fmt.desc}</div>
                    </div>
                  ))}
                </div>
              </div>

              {selectedPlans.length > 0 && (
                <div style={s.summaryCard}>
                  <div style={s.summaryCardTitle}>SUMMARY</div>
                  <div style={s.summaryCardRow}>
                    <span style={s.summaryLabel}>Plans selected</span>
                    <span style={s.summaryValue}>{selectedPlans.length}</span>
                  </div>
                  <div style={s.summaryCardRow}>
                    <span style={s.summaryLabel}>Est. files</span>
                    <span style={s.summaryValue}>{totalFiles * formats.length}</span>
                  </div>
                  {selectedPlans.includes('SSP') && (
                    <div style={s.sspNote}>SSP includes 16 files + 1 merged PDF</div>
                  )}
                </div>
              )}
            </div>

            {/* RIGHT */}
            <div style={s.rightCol}>
              <div style={s.card}>
                <div style={s.cardTitleRow}>
                  <div style={s.cardTitle}>02 — SELECT PLANS</div>
                  <div style={s.planActions}>
                    <button style={s.smallBtn} onClick={selectAll}>All</button>
                    <button style={s.smallBtn} onClick={clearAll}>Clear</button>
                  </div>
                </div>
                <div style={s.planList}>
                  {PLANS.map(plan => (
                    <div
                      key={plan.code}
                      style={{
                        ...s.planItem,
                        ...(selectedPlans.includes(plan.code) ? s.planItemActive : {}),
                        ...(plan.highlight ? s.planItemHighlight : {}),
                      }}
                      onClick={() => togglePlan(plan.code)}
                    >
                      <div style={{ ...s.checkbox, ...(selectedPlans.includes(plan.code) ? s.checkboxActive : {}) }}>
                        {selectedPlans.includes(plan.code) && '✓'}
                      </div>
                      <div style={s.planInfo}>
                        <div style={s.planCode}>{plan.code}</div>
                        <div style={s.planLabel}>{plan.label}</div>
                      </div>
                      <div style={s.planCount}>{plan.count}</div>
                    </div>
                  ))}
                </div>
              </div>

              {error && <div style={s.error}>{error}</div>}

              {!result ? (
                <button
                  style={{ ...s.generateBtn, opacity: loading ? 0.7 : 1 }}
                  onClick={handleGenerate}
                  disabled={loading}
                >
                  {loading ? '⏳  Generating plans...' : '⚡  GENERATE PLANS'}
                </button>
              ) : (
                <div style={s.resultBox}>
                  <div style={s.resultTitle}>✓ Generation complete</div>
                  <div style={s.resultList}>
                    {result.results?.map(r => (
                      <div key={r.plan} style={s.resultItem}>
                        <span style={{ color: r.status === 'ok' ? 'var(--green)' : '#ff6b7a' }}>
                          {r.status === 'ok' ? '✓' : '✗'}
                        </span>
                        <span style={s.resultLabel}>{r.label}</span>
                        <span style={s.resultCount}>
                          {r.files?.length || 0} file{r.files?.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                  <button style={s.downloadBtn} onClick={handleDownload}>⬇  DOWNLOAD ZIP</button>
                  <button style={s.resetBtn} onClick={() => { setResult(null); setSelectedPlans([]); }}>
                    Generate Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </main>
      )}

      {/* ── TEMPLATES TAB ── */}
      {activeTab === 'templates' && (
        <main style={s.main}>
          <input
            ref={fileInputRef}
            type="file"
            accept=".docx"
            style={{ display: 'none' }}
            onChange={handleFileChange}
          />

          <div style={s.tplHeader}>
            <div>
              <div style={s.tplTitle}>Template Management</div>
              <div style={s.tplSubtitle}>Upload or replace .docx templates for each plan type</div>
            </div>
            <button style={s.refreshBtn} onClick={loadTemplates} disabled={templatesLoading}>
              {templatesLoading ? '...' : '↺ Refresh'}
            </button>
          </div>

          {uploadSuccess && (
            <div style={s.successMsg}>
              <span style={{ color: 'var(--green)' }}>✓</span> {uploadSuccess}
            </div>
          )}
          {uploadError && <div style={s.error}>{uploadError}</div>}

          {templatesLoading ? (
            <div style={s.tplLoading}>Loading templates...</div>
          ) : (
            <div style={s.tplList}>
              {templates.map(plan => {
                const isExpanded = expandedPlan === plan.code;
                const uploadedCount = plan.files.filter(f => f.exists).length;
                const totalCount = plan.files.length;
                const allUploaded = uploadedCount === totalCount;
                return (
                  <div key={plan.code} style={s.tplCard}>
                    {/* Plan header row */}
                    <div
                      style={s.tplCardHeader}
                      onClick={() => setExpandedPlan(isExpanded ? null : plan.code)}
                    >
                      <div style={s.tplPlanLeft}>
                        <div style={s.tplCodeBadge}>{plan.code}</div>
                        <div>
                          <div style={s.tplPlanLabel}>{plan.label}</div>
                          <div style={s.tplPlanMeta}>{uploadedCount}/{totalCount} templates uploaded</div>
                        </div>
                      </div>
                      <div style={s.tplPlanRight}>
                        <div style={{
                          ...s.tplStatusDot,
                          background: allUploaded ? 'var(--green)' : uploadedCount > 0 ? 'var(--gold)' : '#ff6b7a'
                        }} />
                        <span style={s.tplExpandIcon}>{isExpanded ? '▲' : '▼'}</span>
                      </div>
                    </div>

                    {/* Expanded file list */}
                    {isExpanded && (
                      <div style={s.tplFileList}>
                        {plan.files.map(file => (
                          <div key={file.name} style={s.tplFileRow}>
                            <div style={s.tplFileDot}>
                              <span style={{ color: file.exists ? 'var(--green)' : '#ff6b7a', fontSize: '10px' }}>
                                {file.exists ? '●' : '○'}
                              </span>
                            </div>
                            <div style={s.tplFileInfo}>
                              <div style={s.tplFileName}>{file.name}</div>
                              {file.exists ? (
                                <div style={s.tplFileMeta}>
                                  {formatBytes(file.size)} · {formatDate(file.modified)}
                                </div>
                              ) : (
                                <div style={s.tplFileMissing}>Not uploaded</div>
                              )}
                            </div>
                            <button
                              style={{
                                ...s.tplUploadBtn,
                                opacity: uploadingFile === file.name ? 0.6 : 1,
                              }}
                              onClick={() => handleUploadClick(plan.code, file.name)}
                              disabled={uploadingFile === file.name}
                            >
                              {uploadingFile === file.name
                                ? 'Uploading...'
                                : file.exists ? '↑ Replace' : '↑ Upload'}
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </main>
      )}
    </div>
  );
}

const s = {
  wrapper: { minHeight: '100vh', background: 'var(--navy)' },
  completenessWarn: {
    marginTop: '8px', fontSize: '11px', color: '#ff6b7a',
    fontFamily: 'var(--font-mono)', lineHeight: 1.5,
  },
  header: {
    background: 'var(--navy-mid)', borderBottom: '1px solid var(--navy-border)',
    padding: '0 40px', height: '64px', display: 'flex', alignItems: 'center', gap: '20px',
  },
  backBtn: { background: 'transparent', border: 'none', color: 'var(--grey)', cursor: 'pointer', fontSize: '13px' },
  headerTitle: { fontSize: '16px', fontWeight: '700', flex: 1 },
  headerBadge: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--gold)',
    border: '1px solid var(--gold)', padding: '4px 10px', borderRadius: '4px',
  },
  tabBar: {
    display: 'flex', gap: '4px',
    background: 'var(--navy)', borderRadius: '8px', padding: '4px',
  },
  tab: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    padding: '6px 16px', borderRadius: '6px', fontSize: '11px',
    fontWeight: '600', letterSpacing: '1px', cursor: 'pointer',
    fontFamily: 'var(--font-mono)', transition: 'all 0.2s',
  },
  tabActive: { background: 'var(--navy-mid)', color: 'var(--white)' },
  main: { padding: '40px', maxWidth: '1100px', margin: '0 auto' },
  layout: { display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '24px' },
  leftCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
  rightCol: { display: 'flex', flexDirection: 'column', gap: '20px' },
  card: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '24px',
  },
  cardTitle: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: '600',
    letterSpacing: '2px', color: 'var(--gold)', marginBottom: '16px',
  },
  cardTitleRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' },
  planActions: { display: 'flex', gap: '8px' },
  smallBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '4px 12px', borderRadius: '4px',
    fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  select: {
    width: '100%', background: 'var(--navy)', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '11px 14px', color: 'var(--white)',
    fontSize: '13px', outline: 'none', fontFamily: 'var(--font-mono)',
  },
  vesselSummary: {
    marginTop: '16px', padding: '14px', background: 'var(--navy)',
    borderRadius: '8px', display: 'flex', flexDirection: 'column', gap: '8px',
  },
  summaryRow: { display: 'flex', justifyContent: 'space-between', fontSize: '12px' },
  summaryLabel: { color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  formatRow: { display: 'flex', gap: '12px' },
  formatCard: {
    flex: 1, border: '1px solid var(--navy-light)', borderRadius: '10px',
    padding: '16px', cursor: 'pointer', textAlign: 'center', transition: 'all 0.2s',
  },
  formatCardActive: { borderColor: 'var(--green)', background: 'var(--green-dim)' },
  formatIcon: { fontSize: '22px', marginBottom: '6px' },
  formatLabel: { fontWeight: '700', fontSize: '13px', marginBottom: '2px' },
  formatDesc: { fontSize: '11px', color: 'var(--grey)' },
  summaryCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', padding: '20px',
  },
  summaryCardTitle: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: '600',
    letterSpacing: '2px', color: 'var(--grey)', marginBottom: '12px',
  },
  summaryCardRow: { display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '8px' },
  summaryValue: { fontFamily: 'var(--font-mono)', fontWeight: '600', color: 'var(--white)' },
  sspNote: {
    marginTop: '8px', fontSize: '11px', color: 'var(--gold)',
    fontFamily: 'var(--font-mono)', paddingTop: '8px',
    borderTop: '1px solid var(--navy-border)',
  },
  planList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  planItem: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '12px 16px', border: '1px solid var(--navy-light)',
    borderRadius: '8px', cursor: 'pointer', transition: 'all 0.2s',
  },
  planItemActive: { borderColor: 'var(--green)', background: 'var(--green-dim)' },
  planItemHighlight: { borderColor: 'rgba(201,168,76,0.3)' },
  checkbox: {
    width: '20px', height: '20px', border: '1px solid var(--navy-light)',
    borderRadius: '4px', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: '12px', fontWeight: '700', flexShrink: 0, transition: 'all 0.2s',
  },
  checkboxActive: { background: 'var(--green)', borderColor: 'var(--green)', color: 'white' },
  planInfo: { flex: 1 },
  planCode: { fontFamily: 'var(--font-mono)', fontSize: '12px', fontWeight: '600', color: 'var(--white)' },
  planLabel: { fontSize: '12px', color: 'var(--grey)', marginTop: '2px' },
  planCount: { fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap' },
  error: {
    background: 'rgba(232,0,29,0.1)', border: '1px solid rgba(232,0,29,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: '#ff6b7a',
  },
  generateBtn: {
    width: '100%', background: 'var(--red)', border: 'none', borderRadius: '10px',
    padding: '16px', color: 'white', fontSize: '14px', fontWeight: '700',
    letterSpacing: '1px', cursor: 'pointer',
  },
  resultBox: {
    background: 'var(--navy-mid)', border: '1px solid rgba(0,196,140,0.25)',
    borderRadius: '12px', padding: '24px',
  },
  resultTitle: { fontSize: '15px', fontWeight: '700', color: 'var(--green)', marginBottom: '16px' },
  resultList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' },
  resultItem: { display: 'flex', gap: '10px', fontSize: '13px', alignItems: 'center' },
  resultLabel: { flex: 1 },
  resultCount: { fontFamily: 'var(--font-mono)', fontSize: '11px', color: 'var(--grey)' },
  downloadBtn: {
    width: '100%', background: 'var(--green)', border: 'none', borderRadius: '8px',
    padding: '13px', color: 'white', fontSize: '13px', fontWeight: '700',
    cursor: 'pointer', marginBottom: '10px',
  },
  resetBtn: {
    width: '100%', background: 'transparent', border: '1px solid var(--navy-light)',
    borderRadius: '8px', padding: '11px', color: 'var(--grey)',
    fontSize: '12px', cursor: 'pointer',
  },

  // ── Templates tab styles ──
  tplHeader: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    marginBottom: '24px',
  },
  tplTitle: { fontSize: '18px', fontWeight: '700', marginBottom: '4px' },
  tplSubtitle: { fontSize: '13px', color: 'var(--grey)' },
  refreshBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '8px 16px', borderRadius: '6px',
    fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-mono)',
  },
  successMsg: {
    background: 'rgba(0,196,140,0.1)', border: '1px solid rgba(0,196,140,0.3)',
    borderRadius: '8px', padding: '12px 16px', fontSize: '13px', color: 'var(--green)',
    marginBottom: '16px', display: 'flex', gap: '8px', alignItems: 'center',
  },
  tplLoading: { color: 'var(--grey)', fontSize: '13px', padding: '40px 0', textAlign: 'center' },
  tplList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  tplCard: {
    background: 'var(--navy-mid)', border: '1px solid var(--navy-border)',
    borderRadius: '12px', overflow: 'hidden',
  },
  tplCardHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 20px', cursor: 'pointer', transition: 'background 0.15s',
  },
  tplPlanLeft: { display: 'flex', alignItems: 'center', gap: '14px' },
  tplCodeBadge: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: '700',
    color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.4)',
    padding: '4px 10px', borderRadius: '4px', whiteSpace: 'nowrap',
  },
  tplPlanLabel: { fontSize: '13px', fontWeight: '600', marginBottom: '2px' },
  tplPlanMeta: { fontSize: '11px', color: 'var(--grey)', fontFamily: 'var(--font-mono)' },
  tplPlanRight: { display: 'flex', alignItems: 'center', gap: '12px' },
  tplStatusDot: { width: '8px', height: '8px', borderRadius: '50%' },
  tplExpandIcon: { fontSize: '10px', color: 'var(--grey)' },
  tplFileList: {
    borderTop: '1px solid var(--navy-border)',
    padding: '8px 0',
  },
  tplFileRow: {
    display: 'flex', alignItems: 'center', gap: '12px',
    padding: '10px 20px', transition: 'background 0.1s',
  },
  tplFileDot: { width: '16px', textAlign: 'center', flexShrink: 0 },
  tplFileInfo: { flex: 1, minWidth: 0 },
  tplFileName: {
    fontFamily: 'var(--font-mono)', fontSize: '12px', color: 'var(--white)',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  },
  tplFileMeta: { fontSize: '11px', color: 'var(--grey)', marginTop: '2px' },
  tplFileMissing: { fontSize: '11px', color: '#ff6b7a', marginTop: '2px' },
  tplUploadBtn: {
    background: 'var(--navy)', border: '1px solid var(--navy-light)',
    color: 'var(--white)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '600', cursor: 'pointer',
    fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap', flexShrink: 0,
    transition: 'border-color 0.2s',
  },

  // ── How to Guide button ──
  guideBtn: {
    background: 'transparent', border: '1px solid var(--navy-light)',
    color: 'var(--grey)', padding: '6px 14px', borderRadius: '6px',
    fontSize: '11px', fontWeight: '600', cursor: 'pointer',
    fontFamily: 'var(--font-mono)', whiteSpace: 'nowrap',
    transition: 'border-color 0.2s, color 0.2s',
  },

  // ── Modal overlay & container ──
  overlay: {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
    zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
    padding: '24px',
  },
  modal: {
    background: '#111d2e', border: '1px solid #1e2d40', borderRadius: '16px',
    width: '100%', maxWidth: '760px', maxHeight: '85vh',
    display: 'flex', flexDirection: 'column', overflow: 'hidden',
    boxShadow: '0 24px 80px rgba(0,0,0,0.6)',
  },
  modalHeader: {
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
    padding: '24px 28px 20px', borderBottom: '1px solid #1e2d40', flexShrink: 0,
  },
  modalTitle: { fontSize: '15px', fontWeight: '700', color: '#e8edf4', marginBottom: '4px' },
  modalSubtitle: {
    fontSize: '11px', fontFamily: 'var(--font-mono)', color: 'var(--gold)',
    letterSpacing: '1px',
  },
  modalClose: {
    background: 'transparent', border: 'none', color: 'var(--grey)',
    fontSize: '16px', cursor: 'pointer', padding: '0 0 0 16px', flexShrink: 0,
    lineHeight: 1,
  },
  modalBody: {
    overflowY: 'auto', padding: '24px 28px', display: 'flex',
    flexDirection: 'column', gap: '28px',
  },

  // ── Guide sections ──
  guideSection: {},
  guideSectionTitle: {
    fontFamily: 'var(--font-mono)', fontSize: '10px', fontWeight: '700',
    letterSpacing: '2px', color: 'var(--gold)', marginBottom: '12px',
    textTransform: 'uppercase',
  },
  guideSectionText: { fontSize: '13px', color: '#8a9ab0', lineHeight: '1.7' },
  hl: { color: '#e8edf4' },

  // ── Steps ──
  steps: { display: 'flex', flexDirection: 'column', gap: '14px' },
  step: { display: 'flex', gap: '14px', alignItems: 'flex-start' },
  stepNum: {
    flexShrink: 0, width: '26px', height: '26px', borderRadius: '50%',
    background: 'rgba(201,168,76,0.15)', border: '1px solid rgba(201,168,76,0.35)',
    color: 'var(--gold)', fontSize: '11px', fontWeight: '700',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontFamily: 'var(--font-mono)',
  },
  stepTitle: { fontSize: '13px', fontWeight: '600', color: '#e8edf4', marginBottom: '4px' },
  stepText: { fontSize: '12px', color: '#8a9ab0', lineHeight: '1.65' },

  // ── Plans table ──
  plansTable: { display: 'flex', flexDirection: 'column', gap: '6px' },
  planRow: {
    display: 'flex', gap: '14px', alignItems: 'flex-start',
    padding: '10px 14px', background: '#0d1b2a',
    borderRadius: '8px', border: '1px solid #1e2d40',
  },
  planRowLeft: {
    flexShrink: 0, display: 'flex', flexDirection: 'column', gap: '4px',
    minWidth: '90px', alignItems: 'flex-start',
  },
  planRowCode: {
    fontFamily: 'var(--font-mono)', fontSize: '11px', fontWeight: '700',
    color: 'var(--gold)', border: '1px solid rgba(201,168,76,0.35)',
    padding: '2px 7px', borderRadius: '4px',
  },
  planRowFiles: {
    fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'var(--grey)',
  },
  planRowRight: { flex: 1 },
  planRowDesc: { fontSize: '12px', color: '#8a9ab0', lineHeight: '1.55', marginBottom: '3px' },
  planRowReg: {
    fontFamily: 'var(--font-mono)', fontSize: '10px', color: 'rgba(201,168,76,0.6)',
  },

  // ── Notes ──
  noteList: { display: 'flex', flexDirection: 'column', gap: '8px' },
  noteItem: { display: 'flex', gap: '10px', fontSize: '12px', color: '#8a9ab0', lineHeight: '1.6' },
  noteDot: { color: 'var(--gold)', flexShrink: 0, marginTop: '1px' },
};
